import { Component, input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SharedModule } from '../../shared.module';
import { TooltipModule } from 'primeng/tooltip';
import { SupervisorApiService } from '../../../core/api/services';
import { AlertService } from '../../services/alert.service';
import { Utils } from '../../../core/utils';
import { 
  MasteryCriteriaModel, 
  MaintenanceCriteriaFullModel,
  MaintananceLevelModel 
} from '../../../core/api/models';
import { getPatientTargetStatuses } from '../../utils/utils';

@Component({
  selector: 'app-general-settings-info',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule, TooltipModule],
  templateUrl: './general-settings-info.component.html',
  styleUrl: './general-settings-info.component.scss'
})
export class GeneralSettingsInfoComponent implements OnInit {
  settingsType = input.required<'mastery' | 'maintenance'>();
  
  showDialog = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  
  // Mastery settings
  generalMasteryCriteria = signal<MasteryCriteriaModel | null>(null);
  
  // Maintenance settings
  generalMaintenanceCriteria = signal<MaintenanceCriteriaFullModel | null>(null);
  maintenanceLevels = signal<MaintananceLevelModel[]>([]);
  targetStatuses: any[] = [];

  constructor(
    private supervisorApiService: SupervisorApiService,
    private alertService: AlertService
  ) {
    this.targetStatuses = getPatientTargetStatuses();
  }

  ngOnInit(): void {
    // Component is ready, data will be loaded when dialog opens
  }

  openDialog(): void {
    this.showDialog.set(true);
    this.loadGeneralSettings();
  }

  closeDialog(): void {
    this.showDialog.set(false);
  }

  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.closeDialog();
    }
  }

  loadGeneralSettings(): void {
    this.isLoading.set(true);
    
    if (this.settingsType() === 'mastery') {
      this.loadGeneralMasteryCriteria();
    } else {
      this.loadGeneralMaintenanceCriteria();
    }
  }

  loadGeneralMasteryCriteria(): void {
    this.supervisorApiService.GetMasteryCriteria({
      patientID: undefined
    }).subscribe({
      next: (masteryCriteria) => {
        this.generalMasteryCriteria.set(masteryCriteria);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.alertService.error(Utils.getErrorMessageFromErrorResponse(err));
      }
    });
  }

  loadGeneralMaintenanceCriteria(): void {
    this.supervisorApiService.GetMaintenanceCriteriaFull({
      patientID: undefined
    }).subscribe({
      next: (maintenanceCriteriaFull) => {
        this.generalMaintenanceCriteria.set(maintenanceCriteriaFull);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.alertService.error(Utils.getErrorMessageFromErrorResponse(err));
      }
    });

    this.supervisorApiService.GetMaintananceLevels().subscribe({
      next: (levels) => {
        this.maintenanceLevels.set(levels);
      },
      error: (err) => {
        this.alertService.error(Utils.getErrorMessageFromErrorResponse(err));
      }
    });
  }

  getMaintenanceLevelName(maintenanceLevelID: number | null | undefined): string {
    if (!maintenanceLevelID) {
      return 'N/A';
    }
    return this.maintenanceLevels().find((x) => x.ID === maintenanceLevelID)?.MaintenanceLevel ?? 'N/A';
  }

  getTargetStatusName(statusID: number | null | undefined): string {
    if (statusID === null || statusID === undefined) {
      return 'No change';
    }
    return this.targetStatuses.find((x) => x.value === statusID)?.text ?? 'N/A';
  }
}
